import cn from 'classnames';
import React from 'react';
import { ReactComponent as EllipsedEdgeLarge } from './assets/ellipsedEdgeLarge.svg';
import { ReactComponent as EllipsedEdgeSmall } from './assets/ellipsedEdgeSmall.svg';

export enum EllipsedEdgeColor {
  SOURDOUGH = 'sourdough',
  MINT_200 = 'mint-200',
  MINT_50 = 'mint-50',
  EGGPLANT_500 = 'eggplant-500',
  EGGPLANT = 'eggplant',
  RICE = 'rice',
  RASPBERRY = 'raspberry',
}

const EdgeColor = {
  [EllipsedEdgeColor.SOURDOUGH]: 'text-sourdough',
  [EllipsedEdgeColor.MINT_200]: 'text-mint-200',
  [EllipsedEdgeColor.MINT_50]: 'text-mint-50',
  [EllipsedEdgeColor.EGGPLANT_500]: 'text-eggplant-500',
  [EllipsedEdgeColor.EGGPLANT]: 'text-eggplant',
  [EllipsedEdgeColor.RICE]: 'text-rice',
  [EllipsedEdgeColor.RASPBERRY]: 'text-raspberry',
};

const BackgroundColor = {
  [EllipsedEdgeColor.SOURDOUGH]: 'bg-sourdough',
  [EllipsedEdgeColor.MINT_200]: 'bg-mint-200',
  [EllipsedEdgeColor.MINT_50]: 'bg-mint-50',
  [EllipsedEdgeColor.EGGPLANT_500]: 'bg-eggplant-500',
  [EllipsedEdgeColor.EGGPLANT]: 'bg-eggplant',
  [EllipsedEdgeColor.RICE]: 'bg-rice',
  [EllipsedEdgeColor.RASPBERRY]: 'bg-raspberry',
};

export interface EllipsedEdgeBackgroundProps {
  color: EllipsedEdgeColor;
  ellipsePosition?: 'top' | 'bottom';
  height?: string;
  containerPosition?: 'top' | 'bottom';
}

/**
 * Renders a background with an ellipsed edge at the top or bottom
 *
 * @param color - The color for the background and ellipse (ElliipsedEdgeColor)
 * @param ellipsePosition - The position of the ellipse (top or bottom)
 * @param height - The height of the background (optional, defaults to 100%)
 * @returns
 */
export const EllipsedEdgeBackground: React.FC<EllipsedEdgeBackgroundProps> = ({
  color,
  ellipsePosition = 'top',
  height = '100%',
  containerPosition = 'top',
}) => (
  <div className='absolute left-0 z-background w-full' style={{ height, [containerPosition]: 0 }}>
    <div
      className={cn('flex size-full gap-0', {
        'flex-col-reverse': ellipsePosition === 'bottom',
        'flex-col': ellipsePosition === 'top',
      })}>
      <EllipsedEdgeLarge
        className={cn(EdgeColor[color], 'w-full max-md:hidden', {
          'bottom-0 -mt-0.5 rotate-180': ellipsePosition === 'bottom',
          'top-0 -mb-0.5': ellipsePosition === 'top',
        })}
      />
      <EllipsedEdgeSmall
        className={cn(EdgeColor[color], 'w-full md:hidden', {
          'bottom-0 -mt-0.5 rotate-180': ellipsePosition === 'bottom',
          'top-0 -mb-0.5': ellipsePosition === 'top',
        })}
      />
      <div className={cn('h-full', BackgroundColor[color])}></div>
    </div>
  </div>
);

interface EllipsedEdgeProps extends Pick<EllipsedEdgeBackgroundProps, 'color' | 'ellipsePosition'> {
  children: React.ReactNode;
  className?: string;
}

/**
 * Renders a container with an ellipsed edge at the top or bottom depending on params.
 * Do not use this component if you need to render an ellipse somewhere in the middle of
 * the container. Use EllipsedEdgeBackground instead.
 *
 * @param children - The children to render inside the container
 * @param color - The color of the ellipsed edge - EllipsedEdgeColor
 * @param className - Additional classes to apply to the container
 * @param ellipsePosition - The position of the ellipsed edge - top | bottom
 * @returns
 */
export const EllipsedEdgeContainer: React.FC<EllipsedEdgeProps> = ({
  children,
  color,
  className,
  ellipsePosition = 'top',
}) => (
  <div className={cn('relative isolate w-full overflow-hidden', className)}>
    <EllipsedEdgeBackground color={color} ellipsePosition={ellipsePosition} />
    {children}
  </div>
);
